import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, message, Modal, Form, Input, Rate, Typography, Tag, Progress, Alert, Divider, Radio } from 'antd';
import { CreditCardOutlined, StarOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import paymentScheduleNotificationService from '../../services/paymentScheduleNotificationService';

const { Title, Text } = Typography;
const { TextArea } = Input;

function CustomerScheduleViewer({ bookingId, onPaymentSuccess }) {
    const [schedule, setSchedule] = useState(null);
    const [loading, setLoading] = useState(false);
    const [payingMilestone, setPayingMilestone] = useState(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewForm] = Form.useForm();

    useEffect(() => {
        loadSchedule();
    }, [bookingId]);

    const loadSchedule = async () => {
        try {
            setLoading(true);
            const response = await api.getPaymentSchedule(bookingId);
            setSchedule(response.data);
        } catch (error) {
            message.error('Failed to load payment schedule');
        } finally {
            setLoading(false);
        }
    };

    const handlePayMilestone = async (milestoneId) => {
        try {
            setPayingMilestone(milestoneId);
            await api.payMilestone(bookingId, milestoneId);
            
            // Find the milestone data for notification
            const milestone = schedule?.milestones?.find(m => m.id === milestoneId);
            if (milestone) {
                await paymentScheduleNotificationService.sendMilestonePaymentNotification(bookingId, milestone);
            }
            
            message.success('Payment processed successfully');
            await loadSchedule();
            onPaymentSuccess?.();
        } catch (error) {
            message.error('Payment failed. Please try again.');
        } finally {
            setPayingMilestone(null);
        }
    };

    const handleSubmitReview = async () => {
        try {
            const values = await reviewForm.validateFields();
            await api.createBookingReview(bookingId, values);
            await paymentScheduleNotificationService.sendReviewSubmittedNotification(bookingId, values);
            message.success('Review submitted successfully');
            setShowReviewModal(false);
            reviewForm.resetFields();
        } catch (error) {
            message.error('Failed to submit review');
        }
    };

    const getPaymentStatus = (milestone) => {
        const isPaid = milestone.paid_at;
        const isOverdue = milestone.due_date && dayjs(milestone.due_date).isBefore(dayjs()) && !isPaid;
        
        if (isPaid) {
            return { status: 'paid', color: 'green', text: 'Paid', icon: <CheckCircleOutlined /> };
        } else if (isOverdue) {
            return { status: 'overdue', color: 'red', text: 'Overdue', icon: <ClockCircleOutlined /> };
        } else {
            return { status: 'pending', color: 'orange', text: 'Pending', icon: <ClockCircleOutlined /> };
        }
    };

    const milestoneColumns = [
        {
            title: 'Milestone',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div>
                    <div className="font-medium">{text}</div>
                    <div className="text-sm text-gray-500">{record.description}</div>
                </div>
            ),
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => `₹${amount?.toLocaleString() || 0}`,
        },
        {
            title: 'Due Date',
            dataIndex: 'due_date',
            key: 'due_date',
            render: (date) => date ? dayjs(date).format('MMM DD, YYYY') : '-',
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => {
                const status = getPaymentStatus(record);
                return (
                    <Tag color={status.color} icon={status.icon}>
                        {status.text}
                    </Tag>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                const status = getPaymentStatus(record);
                
                if (status.status === 'paid') {
                    return <Text type="success">Completed</Text>;
                }
                
                return (
                    <Button
                        type="primary"
                        icon={<CreditCardOutlined />}
                        loading={payingMilestone === record.id}
                        onClick={() => handlePayMilestone(record.id)}
                        disabled={status.status === 'overdue'}
                    >
                        Pay Now
                    </Button>
                );
            },
        },
    ];

    const calculateProgress = () => {
        if (!schedule?.milestones) return 0;
        
        const totalAmount = schedule.milestones.reduce((sum, m) => sum + (m.amount || 0), 0);
        const paidAmount = schedule.milestones
            .filter(m => m.paid_at)
            .reduce((sum, m) => sum + (m.amount || 0), 0);
        
        return totalAmount > 0 ? Math.round((paidAmount / totalAmount) * 100) : 0;
    };

    if (loading && !schedule) {
        return <div className="text-center py-8">Loading payment schedule...</div>;
    }

    const progress = calculateProgress();
    const isFullyPaid = progress === 100;
    const isDelivered = schedule?.booking?.status === 'delivered';

    return (
        <div className="space-y-6">
            <Card>
                <div className="flex justify-between items-center mb-4">
                    <Title level={4} className="mb-0">Payment Schedule</Title>
                    {isFullyPaid && isDelivered && (
                        <Button
                            type="primary"
                            icon={<StarOutlined />}
                            onClick={() => setShowReviewModal(true)}
                        >
                            Leave Review
                        </Button>
                    )}
                </div>

                {schedule && (
                    <div className="mb-6">
                        <div className="mb-4">
                            <Text strong>Payment Progress: {progress}%</Text>
                            <Progress percent={progress} status={progress === 100 ? 'success' : 'active'} />
                        </div>
                        
                        <Alert
                            message={`Total Amount: ₹${schedule.total_amount?.toLocaleString() || 0}`}
                            type="info"
                            showIcon
                        />
                    </div>
                )}

                {schedule?.milestones && schedule.milestones.length > 0 && (
                    <Table
                        columns={milestoneColumns}
                        dataSource={schedule.milestones.map((milestone, index) => ({ ...milestone, key: index }))}
                        pagination={false}
                        size="small"
                    />
                )}
            </Card>

            {/* Review Modal */}
            <Modal
                title="Leave a Review"
                open={showReviewModal}
                onCancel={() => setShowReviewModal(false)}
                onOk={handleSubmitReview}
                okText="Submit Review"
                width={600}
            >
                <Form form={reviewForm} layout="vertical">
                    <Form.Item
                        name="rating"
                        label="Overall Rating"
                        rules={[{ required: true, message: 'Please provide a rating' }]}
                    >
                        <Rate />
                    </Form.Item>

                    <Form.Item
                        name="food_quality"
                        label="Food Quality"
                        rules={[{ required: true, message: 'Please rate food quality' }]}
                    >
                        <Rate />
                    </Form.Item>

                    <Form.Item
                        name="service_quality"
                        label="Service Quality"
                        rules={[{ required: true, message: 'Please rate service quality' }]}
                    >
                        <Rate />
                    </Form.Item>

                    <Form.Item
                        name="value_for_money"
                        label="Value for Money"
                        rules={[{ required: true, message: 'Please rate value for money' }]}
                    >
                        <Rate />
                    </Form.Item>

                    <Form.Item
                        name="comment"
                        label="Comments"
                        rules={[{ required: true, message: 'Please provide your comments' }]}
                    >
                        <TextArea
                            placeholder="Share your experience with this catering service..."
                            rows={4}
                        />
                    </Form.Item>

                    <Form.Item
                        name="recommendation"
                        label="Would you recommend this vendor?"
                        rules={[{ required: true, message: 'Please select recommendation' }]}
                    >
                        <Radio.Group>
                            <Radio value="yes">Yes, I would recommend</Radio>
                            <Radio value="no">No, I would not recommend</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}

export default CustomerScheduleViewer;
